import { ResumeReviewFile } from '../db/ResumeReviewFile.js';
import { map, of } from 'rxjs';
import {
  readEntitiesByIDs,
  watchEntitiesByIDs,
  readAllEntities,
  watchAllEntities,
} from '@adornis/buchungsheld-db/server/entity-reads.js';
import { type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';

export const getResumeReviewFilesByIDs = registerQuery({
  operationName: 'getResumeReviewFilesByIDs',
  type: () => [ResumeReviewFile],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<ResumeReviewFile>>(gqlFields: SelSet) =>
      readEntitiesByIDs(ResumeReviewFile, ids, gqlFields),
});

export const subscribeResumeReviewFilesByIDs = registerSubscription({
  operationName: 'subscribeResumeReviewFilesByIDs',
  type: () => [ResumeReviewFile],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<ResumeReviewFile>>(gqlFields: SelSet) =>
      watchEntitiesByIDs(ResumeReviewFile, ids, gqlFields),
});

export const getAllResumeReviewFiles = registerQuery({
  operationName: 'getAllResumeReviewFiles',
  type: () => [ResumeReviewFile],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<ResumeReviewFile>>(gqlFields: SelSet) =>
      readAllEntities(ResumeReviewFile, gqlFields, { skip, limit, excludeIDs }),
});

export const subscribeAllResumeReviewFiles = registerSubscription({
  operationName: 'subscribeAllResumeReviewFiles',
  type: () => [ResumeReviewFile],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<ResumeReviewFile>>(gqlFields: SelSet) =>
      watchAllEntities(ResumeReviewFile, gqlFields, { skip, limit, excludeIDs }),
});

export const getResumeReviewFileByID =
  (id: string | null | undefined) =>
  async <SelSet extends BaseQLSelectionSet<ResumeReviewFile>>(gqlFields: SelSet) => {
    if (!id) return null;
    return (await getResumeReviewFilesByIDs([id])(gqlFields))[0] ?? null;
  };

export const subscribeResumeReviewFileByID =
  (id: string | null | undefined) =>
  <SelSet extends BaseQLSelectionSet<ResumeReviewFile>>(gqlFields: SelSet) =>
    id ? subscribeResumeReviewFilesByIDs([id])(gqlFields).pipe(map(entities => entities[0] ?? null)) : of(null);
